import { type MqttSchema, config } from '@/lib/config';
import type { Context } from '@/lib/store/ui';

/**
 * Which v1 broker topics belong to each tab's Debug Logs view (live mode only —
 * Debug Mode is disabled in mock). Used as the "all topics for this tab"
 * fallback when no specific entity is selected (see useDebugScope).
 *
 * Schema-gated to mirror `subscriptions.ts`'s `buildSubscriptions()` exactly.
 * An `output`-schema tenant never subscribes to the legacy flat topics at
 * all — the backend deletes those sinks outright (ticket 86e2jbaqr) — so a
 * live message can never arrive on them here either. Matching them
 * unconditionally cost nothing functionally (nothing to match), but left
 * the classifier internally inconsistent with what's actually subscribed,
 * which is exactly the kind of drift that produced real debug-tree clutter
 * (legacy `flightplan`/`networkid` rows shown permanently empty on a
 * sandbox tenant that has never subscribed to them).
 *
 * Takes `schema` as a parameter (default `config.mqtt.schema`) rather than
 * reading the module-level config directly, so tests can prove the gating
 * for each schema instead of only exercising whatever `vitest.config.ts`
 * happens to pin.
 */
/** `v1/output/aircraft/{sn}/{kind}` — excludes the per-field alias branch
 *  (`.../drones/{fieldName}`), which carries no unique data and would
 *  swamp the debug log at ~97 msg/s per aircraft. */
const outputAircraft = (t: string, kind: string) =>
  new RegExp(`^v1/output/aircraft/[^/]+/${kind}$`).test(t);

/** `v1/output/events` (tenant-wide) — ticket 86e2jbaqr Parts 3–7. Genuinely
 *  cross-cutting: not scoped to one aircraft or one app context, so it
 *  belongs in every context's debug view, not just `drones`. This is the
 *  topic that fed the "UTM Conflict Detected" alert with no debug trail —
 *  it was subscribed and routed correctly, just invisible in this panel. */
const isOutputEvents = (t: string) => t === 'v1/output/events';

/** `v1/output/alerts/{severity}` — matches both the literal wildcard
 *  subscription string seeded as a tree placeholder (`v1/output/alerts/+`)
 *  and real severity-suffixed topics as they arrive. */
const isOutputAlerts = (t: string) => /^v1\/output\/alerts(\/.+)?$/.test(t);

function buildMatchers(schema: MqttSchema): Partial<Record<Context, (t: string) => boolean>> {
  const matchLegacy = schema !== 'output';
  const matchOutput = schema !== 'legacy';
  return {
    drones: (t) =>
      (matchLegacy &&
        (/^networkid\//.test(t) ||
          t === 'traffic_control' ||
          /^TISB Stream\//.test(t) ||
          // The drone's OI polygon is sourced from `flightplan/<UAS_ID>`,
          // so the flight plan is something the drone context consumes.
          /^flightplan\//.test(t))) ||
      // MAVLink backfill is not schema-gated — flysafe/v1/# is subscribed
      // unconditionally in subscriptions.ts (SHARED_TOPICS) regardless of
      // mqtt.schema, so it stays unconditional here too.
      /^flysafe\/v1\//.test(t) ||
      (matchOutput &&
        // v1/output equivalents (ticket 86e2jbe3d): networkid is identity +
        // position, telemetry is airframe state, and on this namespace the
        // OI volume rides on telemetry too. No per-aircraft `events` match
        // — that topic was a corrected developer mistake; events are
        // tenant-wide only (`isOutputEvents` below).
        (outputAircraft(t, 'networkid') ||
          outputAircraft(t, 'telemetry') ||
          // ETA to the next waypoint and each waypoint reached (ENG-173) — their
          // own per-aircraft topics. The alerts-key copy of `waypoint_approached`
          // is matched below with the other `v1/output/alerts/…` topics.
          outputAircraft(t, 'waypoint_pred') ||
          outputAircraft(t, 'waypoint_approached') ||
          isOutputEvents(t) ||
          isOutputAlerts(t))),
    // OI consumes flightplan (polygon + schedule) AND networkid (pilot /
    // mission / op-type / legal-rule metadata, via networkidToOiUpdate).
    // On the v1/output namespace the polygon arrives on `telemetry`
    // (`operational_volume`) because `oi/declaration` is not yet enabled.
    // Tenant-wide events/alerts included too — several observed samples are
    // `category: "OI"` (e.g. UTM declaration conflicts).
    oi: (t) =>
      (matchLegacy && (/^flightplan\//.test(t) || /^networkid\//.test(t))) ||
      (matchOutput &&
        (outputAircraft(t, 'networkid') ||
          outputAircraft(t, 'telemetry') ||
          /^v1\/output\/aircraft\/[^/]+\/oi\/declaration$/.test(t) ||
          outputAircraft(t, 'waypoint_pred') ||
          outputAircraft(t, 'waypoint_approached') ||
          isOutputEvents(t) ||
          isOutputAlerts(t))),
    surv: (t) =>
      // Surveillance hasn't moved namespace yet (still on the legacy flat
      // paths regardless of mqtt.schema — see subscriptions.ts
      // SHARED_TOPICS), so these two stay unconditional.
      /^surveillance_traffic\//.test(t) ||
      /^surveillance_sensors\//.test(t) ||
      // RF drone detection (ENG-253) publishes under the ingest namespace.
      /^v1\/ingest\/live_rf\//.test(t) ||
      // Observed sample: category "TISB", contexts: ["drones","surveillance"].
      (matchOutput && (isOutputEvents(t) || isOutputAlerts(t))),
  };
}

export function topicMatchesContext(
  topic: string,
  ctx: Context,
  schema: MqttSchema = config.mqtt.schema,
): boolean {
  return buildMatchers(schema)[ctx]?.(topic) ?? false;
}

/** True when the context has a debug topic group (toggle + panel engage). */
export function isDebugContext(ctx: Context): boolean {
  return ctx in buildMatchers(config.mqtt.schema);
}
